import { ContentType } from "src/views/assistant/types";

/**
 * Maps backend content_type strings (including legacy aliases) to canonical
 * ContentType enum values. This is broader than the XML-focused `contentTags`
 * in types.ts because it also handles native tool-calling protocol labels.
 */
const CONTENT_TYPE_ALIASES: Record<string, ContentType> = {
  text: ContentType.TEXT,
  think: ContentType.THINK,
  thought: ContentType.THINK,
  thinking: ContentType.THINK,
  tool_call: ContentType.TOOL_CALL,
  function_call: ContentType.TOOL_CALL,
  tool_response: ContentType.TOOL_RESPONSE,
  function_response: ContentType.TOOL_RESPONSE,
  tool_result: ContentType.TOOL_RESPONSE,
  user_selection_prompt: ContentType.USER_SELECTION_PROMPT,
};

/** Structured tool result types that appear as `payload.type` in tool responses. */
const TOOL_RESULT_TYPES = new Set([
  "user_selection_prompt",
  "web_search_approval",
  "plan_template_publish_approval",
  "file_download",
  "background_task",
]);

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Convert Python dict literals (None, True, False, single-quoted strings)
 * to valid JSON. Some backend tool responses arrive in this format.
 */
function toJsonFromPythonLiteral(text: string): string {
  return text
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_, content: string) => {
      const unescaped = content
        .replace(/\\'/g, "'")
        .replace(/\\"/g, '"')
        .replace(/"/g, '\\"');
      return `"${unescaped}"`;
    });
}

export function parseObjectCandidate(
  text: string,
): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(text);
    return isObjectRecord(parsed) ? parsed : null;
  } catch {
    // Fall back to Python-dict-like payloads occasionally emitted by tools/models.
    try {
      const normalized = toJsonFromPythonLiteral(text);
      const parsed = JSON.parse(normalized);
      return isObjectRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}

/**
 * Parse the last complete JSON object from text that may contain concatenated
 * JSON blobs (e.g. early detection + final indicator: `{"name":"x"}{"name":"x","arguments":{...}}`).
 * Returns null if no valid object can be extracted.
 */
export function parsePayloadObject(
  text: string | null | undefined,
): Record<string, unknown> | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;

  const parsedDirect = parseObjectCandidate(trimmed);
  if (parsedDirect) return parsedDirect;

  // Scan backward for the last parseable top-level `{` boundary.
  for (let pos = trimmed.length - 1; pos >= 0; ) {
    const braceIdx = trimmed.lastIndexOf("{", pos);
    if (braceIdx === -1) break;
    const parsed = parseObjectCandidate(trimmed.slice(braceIdx));
    if (parsed) return parsed;
    pos = braceIdx - 1;
  }

  return null;
}

/**
 * Extract the tool_call_id from a tool_call or tool_response part's text.
 *
 * Parts accumulate text (early indicator + progress + final JSON for calls),
 * so this parses the LAST complete JSON object. Returns null when the text
 * has no parseable payload or the payload carries no id — callers fall back
 * to positional pairing for legacy content.
 */
export function extractToolCallId(
  text: string | null | undefined,
): string | null {
  const parsed = parsePayloadObject(text);
  const id = parsed?.tool_call_id;
  return isNonEmptyString(id) ? (id as string) : null;
}

export function looksLikeToolCallPayload(
  payload: Record<string, unknown>,
): boolean {
  if (!isNonEmptyString(payload.name)) return false;

  const hasArguments = isObjectRecord(payload.arguments);
  const hasToolCallId = isNonEmptyString(payload.tool_call_id);
  const hasStatusMessage = isNonEmptyString(payload.status_message);

  return hasArguments || hasToolCallId || hasStatusMessage;
}

export function looksLikeToolResponsePayload(
  payload: Record<string, unknown>,
): boolean {
  if (payload.message_type === "tool_result") return true;

  if (isNonEmptyString(payload.tool_call_id)) {
    return (
      "result" in payload ||
      "status_summary" in payload ||
      typeof payload.tool_name === "string" ||
      typeof payload.error === "string"
    );
  }

  return (
    typeof payload.type === "string" && TOOL_RESULT_TYPES.has(payload.type)
  );
}

/**
 * Resolve a content part's effective type: TEXT parts whose text is actually
 * a JSON tool payload are treated as TOOL_CALL/TOOL_RESPONSE. Mirrors the
 * render-time safety net in AssistantPart so grouping logic sees the same
 * types the renderer does.
 */
export function resolveEffectivePartType(part: {
  type: ContentType;
  text?: string | null;
}): ContentType {
  if (part.type !== ContentType.TEXT) return part.type;
  const trimmed = (part.text ?? "").trim();
  if (!trimmed.startsWith("{")) return part.type;
  const parsed = parsePayloadObject(trimmed);
  if (!parsed) return part.type;
  if (looksLikeToolResponsePayload(parsed)) return ContentType.TOOL_RESPONSE;
  if (looksLikeToolCallPayload(parsed)) return ContentType.TOOL_CALL;
  return part.type;
}

/**
 * Normalize a raw content_type string from the backend into a canonical
 * ContentType enum value. Returns null for unknown or missing values.
 */
export function normalizeContentType(
  rawContentType: string | null | undefined,
): ContentType | null {
  if (!rawContentType) return null;
  return (
    CONTENT_TYPE_ALIASES[String(rawContentType).trim().toLowerCase()] ?? null
  );
}

/**
 * Infer the content type from the chunk text itself by inspecting XML tags
 * or JSON payload shape. Used as a fallback when `content_type` is missing.
 */
export function inferContentTypeFromChunk(
  chunkText: string | null | undefined,
): ContentType | null {
  if (!chunkText) return null;
  const trimmed = chunkText.trimStart();
  if (!trimmed) return null;

  // Legacy XML-tagged protocol
  if (
    trimmed.startsWith("<tool_call>") ||
    trimmed.startsWith("<function_call>")
  ) {
    return ContentType.TOOL_CALL;
  }
  if (trimmed.startsWith("<tool_response>")) {
    return ContentType.TOOL_RESPONSE;
  }
  if (trimmed.startsWith("<think>") || trimmed.startsWith("<thought>")) {
    return ContentType.THINK;
  }

  // Native JSON protocol: inspect payload shape
  const parsed = parsePayloadObject(trimmed);
  if (!parsed) return null;

  // Check response before call -- a response with tool_call_id could also
  // have a `name` field, so response detection must come first.
  if (looksLikeToolResponsePayload(parsed)) return ContentType.TOOL_RESPONSE;
  if (looksLikeToolCallPayload(parsed)) return ContentType.TOOL_CALL;

  return null;
}

/**
 * Resolve content type by first normalizing the explicit `rawContentType`
 * label, then falling back to inference from the chunk text.
 */
export function resolveContentType(
  rawContentType: string | null | undefined,
  chunkText: string | null | undefined,
): ContentType | null {
  return (
    normalizeContentType(rawContentType) ?? inferContentTypeFromChunk(chunkText)
  );
}
